import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useJsonLd, faqSchema, breadcrumbSchema } from "@/hooks/useJsonLd";
import { api } from "@/lib/api";
import {
  ArrowRight, BookOpen, Download, CheckCircle2, XCircle, Mail, Flame, Award, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

export default function FreeGuide() {
  useDocumentMeta({
    title: "Free 25 S-98 Practice Questions — APM Fire Alarm Exam Prep",
    description: "Free downloadable practice quiz for the FDNY S-98 Certificate of Fitness. 25 questions with explanations, no card required. Instant access.",
    image: "/og/og-free-guide.png",
    path: "/free-guide",
  });
  useJsonLd([
    faqSchema([
      { q: "Is the S-98 practice quiz really free?",
        a: "Yes. 25 hand-picked S-98 practice questions with full explanations, no credit card. You'll get a copy emailed to you as well." },
      { q: "What is the FDNY S-98 Certificate of Fitness?",
        a: "The S-98 authorizes a holder to supervise the testing and maintenance of fire alarm systems and other related systems in NYC. It's required for many supervisor and inspector roles." },
      { q: "How long is the real FDNY S-98 exam?",
        a: "The FDNY S-98 written exam is approximately 1 hour with about 50 multiple-choice questions. Passing score is 70%." },
      { q: "What's the difference between FDNY S-98 and NICET Level 1?",
        a: "S-98 is a NYC-specific Certificate of Fitness issued by FDNY. NICET Level 1 Foundation is a national certification issued by NICET. The full APM course prepares you for both." },
      { q: "Will these questions appear on the real exam?",
        a: "No — these are practice questions written by our instructor. They cover the same NFPA 72 and NYC Fire Code topics but are not from the official FDNY question bank." },
    ]),
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Free Guide", url: "/free-guide" },
    ]),
  ]);

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Load questions in background so they're ready as soon as user unlocks
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/free-guide-questions");
        setQuestions(data.questions || []);
      } catch {
        // Question prefetch failed; user will see "no questions" once they unlock
      }
    })();
  }, []);

  const submitLead = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        email: trimmed,
        full_name: fullName.trim() || null,
        source: "free_guide",
      });
      setUnlocked(true);
      toast.success("Unlocked. Check your email for the link too.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const pickAnswer = (qIdx, optIdx) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [qIdx]: optIdx }));
  };

  const submitQuiz = () => {
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const score = submitted
    ? questions.reduce((acc, q, i) => acc + (answers[i] === q.answer_index ? 1 : 0), 0)
    : null;
  const pct = submitted ? Math.round((score / questions.length) * 100) : null;
  const passed = pct !== null && pct >= 70;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main data-testid="free-guide-page">
        {/* Hero */}
        <section className="bg-zinc-950 text-white">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
            <div className="overline text-zinc-400 mb-3"><span className="red-bar" />Free practice pack</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]" data-testid="free-guide-headline">
              25 free <span className="text-red-500">S-98 practice questions.</span><br/>
              See if you're ready.
            </h1>
            <p className="text-zinc-300 mt-6 max-w-2xl text-lg leading-relaxed">
              Hand-picked questions covering NFPA 72 fundamentals, NYC code, Class A/B circuits,
              detector spacing, and FACP signal interpretation. No card, no fluff.
            </p>
            <div className="grid grid-cols-3 gap-6 mt-10 max-w-xl">
              {[
                { n: "25", l: "Questions" },
                { n: "100%", l: "Free" },
                { n: "70%+", l: "Means you're close" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-red-600 pl-3">
                  <div className="font-heading text-3xl font-bold tracking-tight">{s.n}</div>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          {!unlocked ? (
            // ── Email-gate form ──────────────────────────────────────────
            <div data-testid="free-guide-gate">
              <div className="card-tech p-8">
                <div className="overline mb-2"><span className="red-bar" />Unlock the questions</div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">
                  Where should we send the link?
                </h2>
                <p className="text-sm text-zinc-700 mb-6">
                  We'll email a copy you can come back to anytime. No spam — Trevor sends maybe 1 email per month with new questions and updates.
                </p>
                <form onSubmit={submitLead} className="space-y-4" data-testid="free-guide-form">
                  <label className="block">
                    <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-600">First name (optional)</span>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Your name"
                      className="mt-1 w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 text-sm bg-white"
                      data-testid="free-guide-name-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-600">Email *</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="mt-1 w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 text-sm bg-white"
                      data-testid="free-guide-email-input"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full justify-center"
                    data-testid="free-guide-unlock-btn"
                  >
                    {submitting ? "Unlocking…" : "Unlock 25 questions"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-[11px] font-mono text-zinc-500 text-center">
                    Your email will never be sold. Unsubscribe in one click anytime.
                  </p>
                </form>
              </div>

              {/* Trust row */}
              <div className="grid sm:grid-cols-3 gap-4 mt-8">
                <div className="text-center p-4">
                  <Sparkles className="w-5 h-5 text-red-600 mx-auto mb-2" />
                  <div className="text-xs text-zinc-700">Real exam-level difficulty</div>
                </div>
                <div className="text-center p-4">
                  <Award className="w-5 h-5 text-red-600 mx-auto mb-2" />
                  <div className="text-xs text-zinc-700">From a 15-yr NYC instructor</div>
                </div>
                <div className="text-center p-4">
                  <Mail className="w-5 h-5 text-red-600 mx-auto mb-2" />
                  <div className="text-xs text-zinc-700">PDF copy sent to your email</div>
                </div>
              </div>
            </div>
          ) : (
            // ── Quiz UI (post-unlock) ────────────────────────────────────
            <div data-testid="free-guide-quiz">
              {/* Score banner */}
              {submitted && (
                <div className={`p-6 mb-8 border-l-4 ${passed ? "bg-green-50 border-green-600" : "bg-amber-50 border-amber-500"}`} data-testid="free-guide-score">
                  <div className="flex items-start gap-4">
                    {passed ? <CheckCircle2 className="w-8 h-8 text-green-600 flex-shrink-0" />
                            : <XCircle className="w-8 h-8 text-amber-600 flex-shrink-0" />}
                    <div className="flex-1">
                      <div className="overline mb-1">Your score</div>
                      <div className="font-heading text-4xl font-bold tracking-tight">
                        {score} / {questions.length} <span className="text-zinc-400 text-2xl">({pct}%)</span>
                      </div>
                      <p className="text-sm text-zinc-700 mt-2 leading-relaxed">
                        {passed
                          ? "You're tracking well — the full APM course will sharpen the rest. Real FDNY S-98 exam is 70% to pass."
                          : "Don't worry — every question below has an explanation. Read those, then come back. The full course covers each topic in depth."}
                      </p>
                      <Link to="/enroll" className="mt-4 inline-block">
                        <button className={`btn-primary`} data-testid="free-guide-enroll-after-quiz">
                          Get the full 140-hr course — $149 <ArrowRight className="w-4 h-4" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Questions */}
              <div className="space-y-8" data-testid="free-guide-questions">
                {questions.map((q, qIdx) => {
                  const picked = answers[qIdx];
                  const correct = q.answer_index;
                  return (
                    <div key={q.q?.slice(0, 40) || `fg-q-${qIdx}`} className="card-tech p-6" data-testid={`fg-q-${qIdx}`}>
                      <div className="flex items-baseline gap-3 mb-4">
                        <span className="font-heading text-xl font-bold text-red-600">{String(qIdx + 1).padStart(2, "0")}</span>
                        <h3 className="font-heading text-lg font-semibold tracking-tight leading-snug">{q.q}</h3>
                      </div>
                      <div className="space-y-2 ml-9">
                        {q.options.map((opt, optIdx) => {
                          const isPicked = picked === optIdx;
                          const isCorrect = optIdx === correct;
                          let cls = "border-zinc-200 hover:border-red-300";
                          if (submitted) {
                            if (isCorrect) cls = "border-green-600 bg-green-50";
                            else if (isPicked && !isCorrect) cls = "border-red-600 bg-red-50";
                            else cls = "border-zinc-200";
                          } else if (isPicked) {
                            cls = "border-red-600 bg-red-50";
                          }
                          return (
                            <button
                              key={`fg-q${qIdx}-opt${optIdx}`}
                              onClick={() => pickAnswer(qIdx, optIdx)}
                              disabled={submitted}
                              className={`w-full text-left p-3 border-2 transition-colors ${cls} ${submitted ? "cursor-default" : "cursor-pointer"}`}
                              data-testid={`fg-q-${qIdx}-opt-${optIdx}`}
                            >
                              <span className="font-mono text-xs text-zinc-500 mr-2">{String.fromCharCode(65 + optIdx)}.</span>
                              <span className="text-sm">{opt}</span>
                              {submitted && isCorrect && <CheckCircle2 className="w-4 h-4 text-green-600 inline ml-2" />}
                              {submitted && isPicked && !isCorrect && <XCircle className="w-4 h-4 text-red-600 inline ml-2" />}
                            </button>
                          );
                        })}
                      </div>
                      {submitted && (
                        <div className="ml-9 mt-3 p-3 bg-zinc-50 border-l-2 border-zinc-300">
                          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Explanation</div>
                          <p className="text-sm text-zinc-700 leading-relaxed">{q.explanation}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Submit / final CTA */}
              {!submitted ? (
                <div className="mt-10 text-center">
                  <button
                    onClick={submitQuiz}
                    disabled={Object.keys(answers).length < questions.length}
                    className="btn-primary"
                    data-testid="free-guide-submit-btn"
                  >
                    {Object.keys(answers).length < questions.length
                      ? `Answer all ${questions.length} questions to submit (${Object.keys(answers).length}/${questions.length})`
                      : "Score my quiz"} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="mt-10 bg-zinc-950 text-white p-8 text-center">
                  <Flame className="w-10 h-10 text-red-500 mx-auto mb-3" />
                  <h3 className="font-heading text-2xl font-bold tracking-tight mb-2">Ready for the real thing?</h3>
                  <p className="text-sm text-zinc-300 mb-6 max-w-md mx-auto">
                    The full APM course has 1000+ questions, interactive labs, an AI tutor, and a printable NICET-aligned certificate.
                  </p>
                  <Link to="/enroll">
                    <button className="btn-primary" data-testid="free-guide-final-enroll-btn">
                      Enroll — $149 lifetime access <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                  <div className="mt-8 pt-6 border-t border-zinc-800 grid sm:grid-cols-3 gap-3 text-sm" data-testid="free-guide-related-links">
                    <Link to="/career" className="text-zinc-300 hover:text-white underline-offset-4 hover:underline" data-testid="fg-related-career">
                      See the full career roadmap →
                    </Link>
                    <Link to="/track/fdny-s95" className="text-zinc-300 hover:text-white underline-offset-4 hover:underline" data-testid="fg-related-s95">
                      Browse the S-95 track →
                    </Link>
                    <Link to="/about-instructor" className="text-zinc-300 hover:text-white underline-offset-4 hover:underline" data-testid="fg-related-instructor">
                      Meet your instructor →
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
